/**
 * Final TS-ZERO sweep shared helpers (cross-bucket leftovers).
 * Follow-up: merge into domain types after Phase 3.
 */

import type { ComponentProps, HTMLAttributes, ReactNode } from 'react';
import type { FormikProps } from 'formik';
import type { RatioDisplay, Image } from 'Components/frameworks/image/Image';
import type { FavoriteProductType } from 'Context/FavoriteProductListContext';
import type { AttendeesType } from 'Store/booking/attendees/attendees.selector';
import type { InitialAttendeesListType } from 'Components/widgets/contact-info/ContactInfoUtils';
import type { Locales } from 'Components/utils/languages/SwitchLanguageModel';
import type { LabeledIcon } from 'Components/frameworks/labeled-icons-list/LabeledIconsList';
import type {
  BELineProps,
  AccommodationsCategory,
} from 'Components/lines/product-accommodations-line/productAccommodations.utils';
import type { FormProduct } from 'Components/lines/prebooking-form-line/PrebookingFormLine.utils';
import type { ThumbnailProduct } from 'Utils/resort-thumbnail-query/resortThumbnailQuery';

/** Generic fixture cast for unit tests. */
export const castAs = <T>(value: unknown): T => value as unknown as T;

/** Generic component-props cast at JSX call sites. */
export const asComponentProps = <T>(props: unknown): T => props as T;

/** Image/Picture props cast (PictureProps is not exported from Image.tsx). */
export type ImageComponentProps = ComponentProps<typeof Image>;
export const asImageProps = (props: unknown): ImageComponentProps => props as ImageComponentProps;

/** Ratio display tuples from CMS card configs. */
export const asRatioDisplay = (value: unknown): RatioDisplay => value as RatioDisplay;

/** aria-level on role="heading" paragraphs (non-standard DOM attr). */
export const asHeadingRoleProps = (
  level: number | string,
): Pick<HTMLAttributes<HTMLParagraphElement>, 'aria-level' | 'role'> =>
  ({ 'aria-level': String(level), role: 'heading' }) as unknown as Pick<
    HTMLAttributes<HTMLParagraphElement>,
    'aria-level' | 'role'
  >;

/** Config.get() return values used as strings. */
export const asConfigString = (value: unknown): string => (value != null ? String(value) : '');

/** localStorage counter values. */
export const asStorageNumber = (value: unknown): number =>
  typeof value === 'number' ? value : Number(value) || 0;

/** Partial validation schema entries used in contact-info specs. */
export const asValidationSchema = (
  field: { name: string } & Record<string, unknown>,
): ValidationSchema => field as unknown as ValidationSchema;

/** Partial attendees fixtures in contact-info / booking specs. */
export const asAttendeesType = (fixture: unknown): AttendeesType =>
  fixture as unknown as AttendeesType;

/** Partial initial attendees list in contact-info specs. */
export const asInitialAttendeesList = (fixture: unknown): InitialAttendeesListType =>
  fixture as unknown as InitialAttendeesListType;

/** Locales map stubs in SwitchLanguageModel specs. */
export const asLocales = (fixture: unknown): Locales => fixture as unknown as Locales;

/** Formik props stubs in contact-info utils specs. */
export const asFormikProps = (fixture: unknown): FormikProps<Record<string, unknown>> =>
  fixture as unknown as FormikProps<Record<string, unknown>>;

/** Travel companion partial fixtures. */
export const asTravelCompanions = (fixture: unknown): TravelCompanion[] =>
  fixture as unknown as TravelCompanion[];

/** Activity entity partial fixtures. */
export const asActivityEntity = (fixture: unknown): ActivityEntity =>
  fixture as unknown as ActivityEntity;

/** Activities category partial fixtures. */
export const asActivitiesCategoryEntity = (fixture: unknown): ActivitiesCategoryEntity =>
  fixture as unknown as ActivitiesCategoryEntity;

/** Accommodation entity partial fixtures. */
export const asAccommodationEntity = (fixture: unknown): AccommodationEntity =>
  fixture as unknown as AccommodationEntity;

/** Product facility partial fixtures. */
export const asProductFacility = (fixture: unknown): ProductFacility => fixture as ProductFacility;

/** CMS line props partial fixtures. */
export const asBELineProps = (props: unknown): BELineProps => props as BELineProps;

/** CMS content partial fixtures. */
export const asCmsContent = (content: unknown): CMSContent => content as CMSContent;

/** Legal information partial fixtures. */
export const asLegalInformations = (items: unknown): LegalInformation[] =>
  items as LegalInformation[];

/** Thumbnail product partial fixtures. */
export const asThumbnailProducts = (products: unknown): readonly ThumbnailProduct[] =>
  products as readonly ThumbnailProduct[];

/** Form product partial fixtures. */
export const asFormProduct = (product: unknown): FormProduct => product as FormProduct;

/** Labeled icon partial fixtures. */
export const asLabeledIcons = (icons: unknown): LabeledIcon[] => icons as LabeledIcon[];

/** mapColToCard input (untyped destructured params). */
export const asMapColInput = (props: unknown): Record<string, unknown> =>
  props as Record<string, unknown>;

/** Accommodations category partial fixtures. */
export const asAccommodationsCategories = (categories: unknown): AccommodationsCategory[] =>
  categories as AccommodationsCategory[];

/** Favorite product partial fixtures in booking utils specs. */
export const asFavoriteProductType = (fixture: unknown): FavoriteProductType =>
  fixture as unknown as FavoriteProductType;

/** Booking entity partial fixtures in search/bookmark mappers. */
export const asBookingEntity = (fixture: unknown): BookingEntity =>
  fixture as unknown as BookingEntity;

/** Proposal price partial fixtures in price util specs. */
export const asProposalPrice = (fixture: unknown): ProposalPrice =>
  fixture as unknown as ProposalPrice;

/** Cart rental/childcare partial fixtures in booking additional services specs. */
export const asCartRentals = (fixture: unknown): ReadonlyArray<CartRental | CartChildcare> =>
  fixture as unknown as ReadonlyArray<CartRental | CartChildcare>;

/** Didomi preferences surface on window. */
export type DidomiPreferencesWindow = Window & {
  Didomi?: { preferences: { show: () => void } };
};

/** Re-export bucket helpers commonly needed in final sweep. */
export { asPartial } from 'Src/types/reservation';
export { makeWidgetProps, asWidgetProps, asLegacyJsxComponent } from 'Src/types/widget-helpers';
export {
  makeViewProps,
  asProductFixture,
  asProposalFixture,
  asStayEntityFixture,
  asGqlData,
  queryParamAsString,
  asLegacyDomChildren,
} from 'Src/types/view-helpers';
export { castAs as castAsObject, asStayEntity, queryParamToString } from 'Src/types/server-helpers';

/** Legacy DOM Node typed as React children. */
export type LegacyReactChildren = ReactNode;
